import {
  type FormEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  type ApercuManuscrit,
  type Brief,
  briefEcritSchema,
  estEcrite,
  type Livre,
  type LivreDetail,
  MANUSCRIT_MAX,
  type Provenance,
} from "./contract";
import { compter } from "./format";
import { ALERTE_GRISE, BadgeProvenance, BOUTON_CONTOUR, CHAMP } from "./histoires-commun";
import { type BriefEcrit, messageDEchec, useEcrireBrief, useManuscrit } from "./histoires-requetes";
import { groupesParRaison, plages } from "./histoires-textes";

/**
 * L'onglet Brief d'un livre (maquette `HistoiresBrief.dc.html`) : le brief a lire, et a
 * modifier (mots interdits groupes par raison, fourchette, phrases attendues, notes, phrase de
 * style des images) ; les notes du brief ; « Coller un manuscrit », qui montre d'abord ce qu'il
 * remplirait (`essai: true`) et n'ecrit qu'a « Remplir ».
 */

export function OngletBrief({ detail }: { detail: LivreDetail }) {
  const [edition, setEdition] = useState<"lecture" | "edition" | "fermee">("lecture");
  const modifier = useRef<HTMLButtonElement>(null);

  // Sortie du formulaire : le focus revient a « Modifier ».
  useEffect(() => {
    if (edition === "fermee") modifier.current?.focus();
  }, [edition]);

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,38rem)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-5">
        {edition === "edition" ? (
          <FormulaireBrief fermer={() => setEdition("fermee")} livre={detail.livre} />
        ) : (
          <LectureDuBrief
            livre={detail.livre}
            modifier={() => setEdition("edition")}
            ref={modifier}
          />
        )}
        {edition === "edition" ? null : <NotesDuBrief notes={detail.livre.brief.notes} />}
      </div>
      <CollerUnManuscrit detail={detail} />
    </div>
  );
}

function Champ({ nom, children }: { nom: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-3.5 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold text-muted-foreground">{nom}</h3>
      {children}
    </div>
  );
}

function Vide({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

/** La fourchette en clair : « au moins 0, au plus 100 ». */
export function fourchetteEnClair(fourchette: Brief["mots_par_double"]): string | null {
  return fourchette === null ? null : `au moins ${fourchette.min}, au plus ${fourchette.max}`;
}

function LectureDuBrief({
  livre,
  modifier,
  ref,
}: {
  livre: Livre;
  modifier: () => void;
  ref: Ref<HTMLButtonElement>;
}) {
  const { brief } = livre;
  const groupes = groupesParRaison(brief.mots_interdits);
  const fourchette = fourchetteEnClair(brief.mots_par_double);
  return (
    <section
      aria-labelledby="histoires-brief"
      className="flex flex-col gap-3.5 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold" id="histoires-brief">
          Brief
        </h2>
        {/* Un bouton nu : celui du template ne transmet pas de `ref`, et le focus y revient. */}
        <button
          aria-label="Modifier le brief"
          className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
          onClick={modifier}
          ref={ref}
          type="button"
        >
          Modifier
        </button>
      </div>
      <Champ nom="Sujet">
        {brief.sujet.trim() === "" ? (
          <Vide>Pas encore de sujet.</Vide>
        ) : (
          <p className="whitespace-pre-line">{brief.sujet}</p>
        )}
      </Champ>
      <div className="grid gap-4 border-t border-border pt-3.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-muted-foreground">Âges</h3>
          {brief.ages.trim() === "" ? <Vide>non précisés</Vide> : <p>{brief.ages}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-muted-foreground">Mots par double</h3>
          {fourchette === null ? (
            <Vide>pas de fourchette : le compteur compte sans mesurer</Vide>
          ) : (
            <>
              <p className="tabular-nums">{fourchette}</p>
              <p className="text-xs text-muted-foreground">
                le compteur de chaque double s'y mesure
              </p>
            </>
          )}
        </div>
      </div>
      <Champ nom="Fil conducteur">
        {brief.fil.trim() === "" ? (
          <Vide>Pas encore de fil conducteur.</Vide>
        ) : (
          <p className="whitespace-pre-line">{brief.fil}</p>
        )}
      </Champ>
      <Champ
        nom={
          <>
            Mots interdits{" "}
            <span className="font-normal">surlignés dans le texte, avec leur raison</span>
          </>
        }
      >
        {groupes.length === 0 ? (
          <Vide>Aucun mot interdit.</Vide>
        ) : (
          <ul aria-label="Mots interdits" className="flex flex-col">
            {groupes.map((groupe) => (
              <li
                className="flex flex-col gap-1.5 border-t border-dashed border-border py-2 first:border-t-0 first:pt-0.5"
                key={groupe.raison ?? ""}
              >
                <span className="flex flex-wrap gap-1.5">
                  {groupe.mots.map((mot) => (
                    <span
                      className="whitespace-nowrap rounded-md bg-interdit/15 px-2 py-0.5 text-sm font-medium text-interdit"
                      key={mot}
                    >
                      {mot}
                    </span>
                  ))}
                </span>
                {groupe.raison === null ? null : (
                  <span className="text-xs text-muted-foreground">{groupe.raison}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Champ>
      <Champ nom="Phrases attendues">
        {brief.phrases_attendues.length === 0 ? (
          <Vide>Aucune phrase attendue.</Vide>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {brief.phrases_attendues.map((phrase) => (
              <li key={phrase}>{phrase}</li>
            ))}
          </ul>
        )}
      </Champ>
      <Champ
        nom={
          <>
            Phrase de style des images{" "}
            <span className="font-normal">en anglais, commune à toutes les images</span>
          </>
        }
      >
        {livre.style === null || livre.style.trim() === "" ? (
          <Vide>Pas encore de phrase de style.</Vide>
        ) : (
          <p
            className="rounded-md border border-border bg-muted/50 px-3 py-2.5 text-sm leading-relaxed"
            lang="en"
          >
            {livre.style}
          </p>
        )}
      </Champ>
    </section>
  );
}

/** Une note du brief : un paragraphe, et son titre en gras quand il commence par `**Titre.**`. */
export function noteEnMorceaux(note: string): {
  readonly titre: string | null;
  readonly corps: string;
} {
  const trouve = /^\*\*(.+?)\*\*\s*([\s\S]*)$/.exec(note);
  if (trouve === null) return { titre: null, corps: note };
  return { titre: trouve[1] ?? null, corps: trouve[2] ?? "" };
}

/** Les notes en paragraphes, separes par une ligne vide. */
export function notesEnParagraphes(notes: string): string[] {
  return notes
    .split(/\n\s*\n/)
    .map((paragraphe) => paragraphe.trim())
    .filter((paragraphe) => paragraphe !== "");
}

const NOTES_VISIBLES = 4;

function Note({ note }: { note: string }) {
  const { titre, corps } = noteEnMorceaux(note);
  return (
    <p className="whitespace-pre-line border-t border-border py-2.5 text-sm first:border-t-0 first:pt-0">
      {titre === null ? null : <b className="font-semibold">{titre}</b>}
      {titre === null ? corps : ` ${corps}`}
    </p>
  );
}

function NotesDuBrief({ notes }: { notes: string }) {
  const paragraphes = notesEnParagraphes(notes);
  const visibles = paragraphes.slice(0, NOTES_VISIBLES);
  const autres = paragraphes.slice(NOTES_VISIBLES);
  return (
    <section
      aria-labelledby="histoires-notes"
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold" id="histoires-notes">
          Notes du brief
        </h2>
        <span className="text-xs text-muted-foreground">
          ce que le grill dit d'autre, en texte libre
        </span>
      </div>
      {paragraphes.length === 0 ? (
        <Vide>
          Pas encore de notes : la voix, le monde, la longueur, ce que Claude relit avant d'écrire.
        </Vide>
      ) : (
        <div className="flex flex-col">
          {visibles.map((note, rang) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: paragraphes figes, jamais reordonnes
            <Note key={rang} note={note} />
          ))}
          {autres.length === 0 ? null : (
            <details className="border-t border-border pt-2.5 text-sm">
              <summary className="cursor-pointer text-muted-foreground">
                {compter(autres.length, "autre note", "autres notes")}
              </summary>
              <div className="mt-2 flex flex-col">
                {autres.map((note, rang) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: paragraphes figes, jamais reordonnes
                  <Note key={rang} note={note} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------
// Modifier le brief.

type Groupe = { readonly cle: number; readonly mots: string; readonly raison: string };

/**
 * Les mots d'un groupe : separes par des virgules, sans les blancs autour ni les vides. Un mot
 * sans caractere ecrit (des espaces insecables, un caractere de largeur nulle d'un copier-coller)
 * compte pour vide : le serveur le refuserait en 400, et tout le brief avec lui.
 */
export function motsDuGroupe(mots: string): string[] {
  return mots
    .split(",")
    .map((mot) => mot.trim())
    .filter((mot) => estEcrite(mot));
}

/** Ce que le formulaire saisit, tel quel. */
export type SaisieDuBrief = {
  readonly sujet: string;
  readonly ages: string;
  readonly fil: string;
  readonly groupes: readonly { readonly mots: string; readonly raison: string }[];
  readonly phrases: string;
  readonly min: string;
  readonly max: string;
  readonly notes: string;
  readonly style: string;
};

const NOM_DU_CHAMP: Readonly<Record<string, string>> = {
  sujet: "le sujet",
  ages: "les âges",
  fil: "le fil conducteur",
  mots_interdits: "les mots interdits",
  phrases_attendues: "les phrases attendues",
  mots_par_double: "la fourchette",
  notes: "les notes",
  style: "la phrase de style",
};

/**
 * La saisie du formulaire, verifiee et mise a la forme du contrat, ou la raison du refus. Le
 * schema du contrat a le dernier mot ; les messages sont d'ici.
 */
export function briefDepuisSaisie(
  saisie: SaisieDuBrief,
):
  | { readonly ok: true; readonly corps: BriefEcrit }
  | { readonly ok: false; readonly refus: string } {
  const min = saisie.min.trim();
  const max = saisie.max.trim();
  let fourchette: { min: number; max: number } | null = null;
  if (min !== "" || max !== "") {
    if (min === "" || max === "") {
      return { ok: false, refus: "Donne les deux bornes de la fourchette, ou aucune." };
    }
    if (!/^\d{1,4}$/.test(min) || !/^\d{1,4}$/.test(max)) {
      return {
        ok: false,
        refus: "Les bornes de la fourchette sont des nombres entiers, de 0 à 2 000.",
      };
    }
    fourchette = { min: Number(min), max: Number(max) };
    if (fourchette.min > fourchette.max) {
      return { ok: false, refus: "Le minimum de la fourchette dépasse le maximum." };
    }
  }
  const motsInterdits = saisie.groupes.flatMap((groupe) =>
    motsDuGroupe(groupe.mots).map((mot) => ({
      mot,
      raison: estEcrite(groupe.raison) ? groupe.raison.trim() : null,
    })),
  );
  const long = motsInterdits.find((interdit) => interdit.mot.length > 80);
  if (long !== undefined) {
    return { ok: false, refus: `Un mot interdit fait 80 caractères au plus : « ${long.mot} ».` };
  }
  const phrases = saisie.phrases
    .split("\n")
    .map((phrase) => phrase.trim())
    .filter((phrase) => phrase !== "");
  const lu = briefEcritSchema.safeParse({
    brief: {
      sujet: saisie.sujet,
      ages: saisie.ages.trim(),
      fil: saisie.fil,
      mots_interdits: motsInterdits,
      phrases_attendues: phrases,
      mots_par_double: fourchette,
      notes: saisie.notes,
    },
    style: saisie.style.trim() === "" ? null : saisie.style.trim(),
  });
  if (lu.success) return { ok: true, corps: lu.data };
  const champs = new Set(
    lu.error.issues.map((probleme) => {
      const champ = String(probleme.path[0] === "brief" ? probleme.path[1] : probleme.path[0]);
      return NOM_DU_CHAMP[champ] ?? champ;
    }),
  );
  return {
    ok: false,
    refus: `Le brief ne passe pas les règles du contrat : ${[...champs].join(", ")} (trop long, ou trop d'entrées).`,
  };
}

function FormulaireBrief({ livre, fermer }: { livre: Livre; fermer: () => void }) {
  const ecrire = useEcrireBrief(livre.id);
  const { brief } = livre;
  const suivant = useRef(0);
  const nouveauGroupe = (mots = "", raison = ""): Groupe => {
    suivant.current += 1;
    return { cle: suivant.current, mots, raison };
  };
  const [sujet, setSujet] = useState(brief.sujet);
  const [ages, setAges] = useState(brief.ages);
  const [fil, setFil] = useState(brief.fil);
  const [groupes, setGroupes] = useState<readonly Groupe[]>(() =>
    groupesParRaison(brief.mots_interdits).map((groupe) =>
      nouveauGroupe(groupe.mots.join(", "), groupe.raison ?? ""),
    ),
  );
  const [phrases, setPhrases] = useState(brief.phrases_attendues.join("\n"));
  const [min, setMin] = useState(
    brief.mots_par_double === null ? "" : String(brief.mots_par_double.min),
  );
  const [max, setMax] = useState(
    brief.mots_par_double === null ? "" : String(brief.mots_par_double.max),
  );
  const [notes, setNotes] = useState(brief.notes);
  const [style, setStyle] = useState(livre.style ?? "");
  const [refus, setRefus] = useState<string | null>(null);
  const premier = useRef<HTMLTextAreaElement>(null);
  const id = useId();

  useEffect(() => {
    premier.current?.focus();
  }, []);

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (ecrire.isPending) return;
    const lu = briefDepuisSaisie({ sujet, ages, fil, groupes, phrases, min, max, notes, style });
    if (!lu.ok) {
      setRefus(lu.refus);
      return;
    }
    setRefus(null);
    ecrire.mutate(lu.corps, { onSuccess: fermer });
  };

  const poserGroupe = (cle: number, changement: Partial<Groupe>): void =>
    setGroupes((avant) =>
      avant.map((groupe) => (groupe.cle === cle ? { ...groupe, ...changement } : groupe)),
    );

  return (
    <section
      aria-labelledby="histoires-brief-edition"
      className="rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="mb-4 text-base font-semibold" id="histoires-brief-edition">
        Modifier le brief
      </h2>
      <form className="flex flex-col gap-4" noValidate onSubmit={envoyer}>
        <ChampDeSaisie etiquette="Sujet" id={`${id}-sujet`}>
          <textarea
            className={cn(CHAMP, "min-h-24")}
            id={`${id}-sujet`}
            maxLength={2000}
            onChange={(evenement) => setSujet(evenement.target.value)}
            ref={premier}
            value={sujet}
          />
        </ChampDeSaisie>
        <div className="grid gap-4 sm:grid-cols-[9rem_minmax(0,1fr)]">
          <ChampDeSaisie etiquette="Âges" id={`${id}-ages`}>
            <input
              className={CHAMP}
              id={`${id}-ages`}
              maxLength={40}
              onChange={(evenement) => setAges(evenement.target.value)}
              placeholder="6 à 9 ans"
              value={ages}
            />
          </ChampDeSaisie>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-semibold">Mots par double</legend>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label htmlFor={`${id}-min`}>au moins</label>
              <input
                className={cn(CHAMP, "w-20 tabular-nums")}
                id={`${id}-min`}
                inputMode="numeric"
                maxLength={4}
                onChange={(evenement) => setMin(evenement.target.value)}
                value={min}
              />
              <label htmlFor={`${id}-max`}>au plus</label>
              <input
                className={cn(CHAMP, "w-20 tabular-nums")}
                id={`${id}-max`}
                inputMode="numeric"
                maxLength={4}
                onChange={(evenement) => setMax(evenement.target.value)}
                value={max}
              />
            </div>
            <p className="text-xs text-muted-foreground">Les deux vides : pas de fourchette.</p>
          </fieldset>
        </div>
        <ChampDeSaisie etiquette="Fil conducteur" id={`${id}-fil`}>
          <textarea
            className={cn(CHAMP, "min-h-20")}
            id={`${id}-fil`}
            maxLength={2000}
            onChange={(evenement) => setFil(evenement.target.value)}
            value={fil}
          />
        </ChampDeSaisie>
        <fieldset className="flex flex-col gap-2.5">
          <legend className="text-sm font-semibold">Mots interdits</legend>
          <p className="text-xs text-muted-foreground">
            Un groupe par raison, les mots ou expressions séparés par des virgules. Ils sont
            surlignés dans le texte de chaque double, avec leur raison ; ils n'empêchent jamais
            d'enregistrer.
          </p>
          {groupes.map((groupe, rang) => (
            <fieldset
              className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
              key={groupe.cle}
            >
              <legend className="sr-only">Groupe {rang + 1}</legend>
              <ChampDeSaisie etiquette="Mots ou expressions" id={`${id}-mots-${groupe.cle}`}>
                <input
                  className={CHAMP}
                  id={`${id}-mots-${groupe.cle}`}
                  onChange={(evenement) =>
                    poserGroupe(groupe.cle, { mots: evenement.target.value })
                  }
                  placeholder="partie, endormie, elle dort"
                  value={groupe.mots}
                />
              </ChampDeSaisie>
              <ChampDeSaisie etiquette="Pourquoi" id={`${id}-raison-${groupe.cle}`}>
                <input
                  className={CHAMP}
                  id={`${id}-raison-${groupe.cle}`}
                  maxLength={300}
                  onChange={(evenement) =>
                    poserGroupe(groupe.cle, { raison: evenement.target.value })
                  }
                  value={groupe.raison}
                />
              </ChampDeSaisie>
              <button
                aria-label={`Retirer le groupe ${rang + 1}`}
                className={cn(BOUTON_CONTOUR, "min-h-9 py-1.5")}
                onClick={() =>
                  setGroupes((avant) => avant.filter((autre) => autre.cle !== groupe.cle))
                }
                type="button"
              >
                Retirer
              </button>
            </fieldset>
          ))}
          <button
            className={cn(BOUTON_CONTOUR, "self-start")}
            onClick={() => setGroupes((avant) => [...avant, nouveauGroupe()])}
            type="button"
          >
            Ajouter un groupe
          </button>
        </fieldset>
        <ChampDeSaisie
          aide="Une par ligne : une réplique dite dans une scène, au moment où il le faut."
          etiquette="Phrases attendues"
          id={`${id}-phrases`}
        >
          <textarea
            className={cn(CHAMP, "min-h-20")}
            id={`${id}-phrases`}
            onChange={(evenement) => setPhrases(evenement.target.value)}
            value={phrases}
          />
        </ChampDeSaisie>
        <ChampDeSaisie
          aide="Le reste du grill, en texte libre : la voix, le monde, la longueur. Un paragraphe par note, une ligne vide entre deux."
          etiquette="Notes du brief"
          id={`${id}-notes`}
        >
          <textarea
            className={cn(CHAMP, "min-h-32")}
            id={`${id}-notes`}
            maxLength={4000}
            onChange={(evenement) => setNotes(evenement.target.value)}
            value={notes}
          />
        </ChampDeSaisie>
        <ChampDeSaisie
          aide="En anglais, commune à toutes les images."
          etiquette="Phrase de style des images"
          id={`${id}-style`}
        >
          <textarea
            className={cn(CHAMP, "min-h-20")}
            id={`${id}-style`}
            lang="en"
            maxLength={1000}
            onChange={(evenement) => setStyle(evenement.target.value)}
            value={style}
          />
        </ChampDeSaisie>
        {refus === null ? null : (
          <p className="text-sm text-destructive" role="alert">
            {refus}
          </p>
        )}
        {ecrire.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(ecrire.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            aria-disabled={ecrire.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="submit"
          >
            Enregistrer le brief
          </Button>
          <button className={BOUTON_CONTOUR} onClick={fermer} type="button">
            Annuler
          </button>
        </div>
      </form>
    </section>
  );
}

function ChampDeSaisie({
  etiquette,
  id,
  aide,
  children,
}: {
  etiquette: string;
  id: string;
  aide?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold" htmlFor={id}>
        {etiquette}
      </label>
      {children}
      {aide === undefined ? null : <p className="text-xs text-muted-foreground">{aide}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Coller un manuscrit.

/** Une ligne de l'apercu, dans l'ordre des numeros. */
type LigneApercu =
  | {
      readonly type: "remplie";
      readonly numero: number;
      readonly titre: string;
      readonly mots: number;
    }
  | {
      readonly type: "intacte";
      readonly numero: number;
      readonly titre: string;
      readonly doubleId: number;
    }
  | { readonly type: "sans_texte"; readonly numero: number; readonly numeros: readonly number[] }
  | { readonly type: "pas_de_double"; readonly numero: number };

/**
 * L'apercu en lignes : les sections sans texte regroupees en plages (« 7 à 16 »), le reste une
 * ligne par numero, « 4. La dernière visite ». Le titre d'une section sans titre est celui de sa
 * double (`titreDe`, le titre range, sans numero).
 */
export function lignesDeLApercu(
  apercu: ApercuManuscrit,
  titreDe: (doubleId: number) => string | null,
): LigneApercu[] {
  const sansTexte = apercu.ignorees
    .filter((ignoree) => ignoree.raison === "sans_texte")
    .map((ignoree) => ignoree.numero)
    .sort((a, b) => a - b);
  const titre = (numero: number, section: string | null, doubleId: number): string => {
    const nom = section?.trim() || titreDe(doubleId)?.trim() || "";
    return nom === "" ? `Double ${numero}` : `${numero}. ${nom}`;
  };
  const lignes: LigneApercu[] = [
    ...apercu.remplies.map(
      (remplie): LigneApercu => ({
        type: "remplie",
        numero: remplie.numero,
        titre: titre(remplie.numero, remplie.titre, remplie.double_id),
        mots: remplie.mots,
      }),
    ),
    ...apercu.intactes.map(
      (intacte): LigneApercu => ({
        type: "intacte",
        numero: intacte.numero,
        titre: titre(intacte.numero, intacte.titre, intacte.double_id),
        doubleId: intacte.double_id,
      }),
    ),
    ...apercu.ignorees
      .filter((ignoree) => ignoree.raison === "pas_de_double")
      .map((ignoree): LigneApercu => ({ type: "pas_de_double", numero: ignoree.numero })),
  ];
  if (sansTexte.length > 0) {
    lignes.push({ type: "sans_texte", numero: sansTexte[0] ?? 0, numeros: sansTexte });
  }
  return lignes.sort((a, b) => a.numero - b.numero);
}

/** « 3 remplies · 3 intactes · 10 sans texte · 1 ignorée » : les parts nulles se taisent. */
export function bilanDeLApercu(apercu: ApercuManuscrit): string {
  const sansTexte = apercu.ignorees.filter((ignoree) => ignoree.raison === "sans_texte").length;
  const ignorees = apercu.ignorees.length - sansTexte;
  return [
    compter(apercu.remplies.length, "remplie", "remplies"),
    apercu.intactes.length > 0 ? compter(apercu.intactes.length, "intacte", "intactes") : null,
    sansTexte > 0 ? `${sansTexte} sans texte` : null,
    ignorees > 0 ? compter(ignorees, "ignorée", "ignorées") : null,
  ]
    .filter((morceau) => morceau !== null)
    .join(" · ");
}

function CollerUnManuscrit({ detail }: { detail: LivreDetail }) {
  const manuscrit = useManuscrit(detail.livre.id);
  const [texte, setTexte] = useState("");
  const [apercu, setApercu] = useState<{ texte: string; resultat: ApercuManuscrit } | null>(null);
  const [rempli, setRempli] = useState<number | null>(null);
  const message = useRef<HTMLParagraphElement>(null);
  const id = useId();

  useEffect(() => {
    if (rempli !== null) message.current?.focus();
  }, [rempli]);

  // Un apercu ne vaut que pour le texte qu'il a lu : changer le texte le retire.
  const apercuValable = apercu !== null && apercu.texte === texte ? apercu.resultat : null;
  const titreDe = (doubleId: number): string | null =>
    detail.doubles.find((candidate) => candidate.id === doubleId)?.titre ?? null;
  const provenanceDe = (doubleId: number) =>
    detail.doubles.find((candidate) => candidate.id === doubleId)?.provenance ?? null;

  const voir = (): void => {
    if (manuscrit.isPending || texte.trim() === "") return;
    setRempli(null);
    manuscrit.mutate(
      { texte, essai: true },
      { onSuccess: (resultat) => setApercu({ texte, resultat }) },
    );
  };

  const remplir = (): void => {
    if (manuscrit.isPending || apercuValable === null) return;
    manuscrit.mutate(
      { texte, essai: false },
      {
        onSuccess: (resultat) => {
          setApercu(null);
          setTexte("");
          setRempli(resultat.remplies.length);
        },
      },
    );
  };

  const remplies = apercuValable?.remplies.length ?? 0;

  return (
    <section
      aria-labelledby="histoires-manuscrit"
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="text-base font-semibold" id="histoires-manuscrit">
        Coller un manuscrit
      </h2>
      <p className="text-sm text-muted-foreground" id={`${id}-aide`}>
        Le texte écrit ailleurs, en Markdown. Chaque titre de niveau 2 qui commence par un numéro va
        dans sa double : « ## 4. La dernière visite » va dans la double 4. Seules les doubles vides
        sont remplies, en « écrit ».
      </p>
      <label className="sr-only" htmlFor={`${id}-texte`}>
        Le manuscrit, en Markdown
      </label>
      <textarea
        aria-describedby={`${id}-aide`}
        className={cn(CHAMP, "min-h-60 font-mono text-xs leading-relaxed")}
        id={`${id}-texte`}
        maxLength={MANUSCRIT_MAX}
        onChange={(evenement) => {
          setTexte(evenement.target.value);
          setRempli(null);
        }}
        spellCheck={false}
        value={texte}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          aria-disabled={manuscrit.isPending || undefined}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          disabled={texte.trim() === ""}
          onClick={voir}
          variant="outline"
        >
          Voir ce qui sera rempli
        </Button>
        <span className="text-xs text-muted-foreground">
          rien n'est enregistré avant « Remplir »
        </span>
      </div>
      {manuscrit.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {messageDEchec(manuscrit.error)}
        </p>
      ) : null}
      {rempli === null ? null : (
        <p className="text-sm" ref={message} role="status" tabIndex={-1}>
          {rempli === 0
            ? "Aucune double remplie : elles avaient toutes déjà un texte."
            : `${compter(rempli, "double remplie", "doubles remplies")}, en « écrit ». Chacune a sa ligne au journal.`}
        </p>
      )}
      {apercuValable === null ? null : (
        <div className="flex flex-col gap-3 border-t border-border pt-3.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Avant d'enregistrer
            </h3>
            <span className="text-xs text-muted-foreground">{bilanDeLApercu(apercuValable)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Ce que le manuscrit remplirait</caption>
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-2 py-1.5 font-semibold" scope="col">
                    Double
                  </th>
                  <th className="px-2 py-1.5 font-semibold" scope="col">
                    Collé
                  </th>
                  <th className="px-2 py-1.5 font-semibold" scope="col">
                    Ce qui se passe
                  </th>
                </tr>
              </thead>
              <tbody>
                {lignesDeLApercu(apercuValable, titreDe).map((ligne) => (
                  <LigneDeLApercu
                    key={`${ligne.type}-${ligne.numero}`}
                    ligne={ligne}
                    provenanceDe={provenanceDe}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className={cn(ALERTE_GRISE, "text-xs")}>
            Ignorés : tout ce qui précède le premier titre de niveau 2 (le titre du fichier, le
            brief, les règles, l'aide-mémoire) et, sous chaque titre, la citation de l'intention et
            la ligne [Image : ].
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              aria-disabled={manuscrit.isPending || undefined}
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              disabled={remplies === 0}
              onClick={remplir}
            >
              {remplies === 0
                ? "Rien à remplir"
                : `Remplir ${compter(remplies, "double", "doubles")}`}
            </Button>
            <button className={BOUTON_CONTOUR} onClick={() => setApercu(null)} type="button">
              Annuler
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Chaque double remplie a sa ligne au journal : toi, manuscrit collé. Pour changer un
            texte déjà là, ouvre sa double. Une double dont le texte a été effacé à la main garde sa
            provenance : elle n'est pas vide pour l'import, « Repartir de zéro » la vide pour de
            bon.
          </p>
        </div>
      )}
    </section>
  );
}

function LigneDeLApercu({
  ligne,
  provenanceDe,
}: {
  ligne: LigneApercu;
  provenanceDe: (doubleId: number) => Provenance | null;
}) {
  const cellule = "border-t border-border px-2 py-2 align-top";
  switch (ligne.type) {
    case "remplie":
      return (
        <tr>
          <td className={cellule}>{ligne.titre}</td>
          <td className={cn(cellule, "tabular-nums")}>{compter(ligne.mots, "mot", "mots")}</td>
          <td className={cellule}>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <strong className="font-semibold">remplie</strong>
              <BadgeProvenance provenance="ecrit" />
            </span>
          </td>
        </tr>
      );
    case "intacte":
      return (
        <tr>
          <td className={cellule}>{ligne.titre}</td>
          <td className={cn(cellule, "text-muted-foreground")}>-</td>
          <td className={cellule}>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground">laissée intacte : a déjà un texte</span>
              <BadgeProvenance provenance={provenanceDe(ligne.doubleId)} />
            </span>
          </td>
        </tr>
      );
    case "sans_texte":
      return (
        <tr>
          <td className={cellule}>{plages(ligne.numeros)}</td>
          <td className={cn(cellule, "text-muted-foreground")}>rien sous le titre</td>
          <td className={cn(cellule, "text-muted-foreground")}>
            {ligne.numeros.length > 1 ? "restent vides" : "reste vide"}
          </td>
        </tr>
      );
    case "pas_de_double":
      return (
        <tr>
          <td className={cn(cellule, "font-mono text-xs")}>## {ligne.numero}.</td>
          <td className={cn(cellule, "text-muted-foreground")}>-</td>
          <td className={cn(cellule, "text-muted-foreground")}>
            ignorée : pas de double {ligne.numero} dans ce livre
          </td>
        </tr>
      );
  }
}
